/* ==========================================================================
   Premium Campaign Popup Pro — Popup Engine
   --------------------------------------------------------------------------
   Vanilla JavaScript. No dependencies. No jQuery.

   Responsibilities:
     - Campaign selection (page visibility, device visibility, date
       scheduling, priority ordering)
     - Delay-based, session-capped popup display (sessionStorage only)
     - Coupon code copy-to-clipboard
     - Full accessibility: focus trap, ESC, overlay click, ARIA state
     - Shopify Theme Editor re-render / preview support
     - Custom DOM events for future analytics integration:
         pcp:popup_open, pcp:popup_close, pcp:campaign_view,
         pcp:coupon_copy, pcp:cta_click
       Listen for these on `document` to wire up Google Analytics, Meta
       Pixel, Microsoft Clarity, or Shopify Pixel without touching this
       file.
   ========================================================================== */

(function () {
  'use strict';

  var BREAKPOINTS = { mobile: 749, tablet: 989 };
  var TRANSITION_FALLBACK_MS = 350;
  var COPY_RESET_MS = 2000;
  var FOCUSABLE_SELECTOR =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  var PAGE_TYPE_MAP = {
    index: 'home',
    collection: 'collection',
    product: 'product',
    cart: 'cart',
    search: 'search',
    page: 'page',
    blog: 'blog',
    article: 'blog'
  };

  /* ------------------------------------------------------------------------
     Helpers
     ------------------------------------------------------------------------ */

  function dispatch(name, detail) {
    try {
      document.dispatchEvent(new CustomEvent(name, { detail: detail || {} }));
    } catch (err) {
      /* CustomEvent unsupported in unusual environments — fail silently */
    }
  }

  function getDeviceType() {
    var width = window.innerWidth;
    if (width <= BREAKPOINTS.mobile) return 'mobile';
    if (width <= BREAKPOINTS.tablet) return 'tablet';
    return 'desktop';
  }

  function parseDateOnly(str) {
    if (!str) return null;
    var parts = String(str).trim().split('-');
    if (parts.length !== 3) return null;
    var year = parseInt(parts[0], 10);
    var month = parseInt(parts[1], 10);
    var day = parseInt(parts[2], 10);
    if (!year || !month || !day) return null;
    var date = new Date(year, month - 1, day, 0, 0, 0, 0);
    if (isNaN(date.getTime())) return null;
    return date;
  }

  function startOfToday() {
    var now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
  }

  function sessionGet(key) {
    try {
      return window.sessionStorage.getItem(key);
    } catch (err) {
      return null;
    }
  }

  function sessionSet(key, value) {
    try {
      window.sessionStorage.setItem(key, value);
    } catch (err) {
      /* sessionStorage unavailable (privacy mode, etc.) — degrade gracefully */
    }
  }

  function isDesignMode() {
    return !!(window.Shopify && window.Shopify.designMode);
  }

  /* ------------------------------------------------------------------------
     Campaign selection
     ------------------------------------------------------------------------ */

  function isCampaignDateActive(campaignEl) {
    var start = parseDateOnly(campaignEl.getAttribute('data-start-date'));
    var end = parseDateOnly(campaignEl.getAttribute('data-end-date'));
    var today = startOfToday();

    if (start && today < start) return false;
    if (end && today > end) return false;
    return true;
  }

  function isCampaignVisibleForPage(campaignEl, pageKey) {
    if (!pageKey) return false;
    var attr = 'data-show-' + pageKey;
    return campaignEl.getAttribute(attr) === 'true';
  }

  function isCampaignVisibleForDevice(campaignEl, device) {
    var attr = 'data-show-' + device;
    return campaignEl.getAttribute(attr) === 'true';
  }

  function isCampaignEnabled(campaignEl) {
    return campaignEl.getAttribute('data-enabled') === 'true';
  }

  function getCampaignPriority(campaignEl) {
    var priority = parseInt(campaignEl.getAttribute('data-priority'), 10);
    return isNaN(priority) ? 5 : priority;
  }

  function getEligibleCampaigns(root, pageKey, device) {
    var campaigns = Array.prototype.slice.call(
      root.querySelectorAll('[data-pcp-campaign]')
    );

    var eligible = campaigns.filter(function (campaignEl) {
      return (
        isCampaignEnabled(campaignEl) &&
        isCampaignVisibleForPage(campaignEl, pageKey) &&
        isCampaignVisibleForDevice(campaignEl, device) &&
        isCampaignDateActive(campaignEl)
      );
    });

    /* Stable sort by priority descending — higher priority shows first */
    eligible = eligible
      .map(function (el, index) {
        return { el: el, index: index, priority: getCampaignPriority(el) };
      })
      .sort(function (a, b) {
        if (b.priority !== a.priority) return b.priority - a.priority;
        return a.index - b.index;
      })
      .map(function (entry) {
        return entry.el;
      });

    return eligible;
  }

  /* ------------------------------------------------------------------------
     PopupInstance
     ------------------------------------------------------------------------ */

  function PopupInstance(root) {
    this.root = root;
    this.overlay = root.querySelector('[data-pcp-overlay]');
    this.popup = root.querySelector('[data-pcp-popup]');
    this.closeBtn = root.querySelector('[data-pcp-close]');
    this.campaignsWrap = root.querySelector('[data-pcp-campaigns]');
    this.sectionId = root.getAttribute('data-section-id');
    this.sessionKey = root.getAttribute('data-session-key');
    this.delay = parseFloat(root.getAttribute('data-delay')) || 0;

    this.activeCampaign = null;
    this.delayTimer = null;
    this.closeTimer = null;
    this.isOpen = false;
    this.lastFocusedElement = null;
    this.forcedPreview = false;

    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleOverlayClick = this.handleOverlayClick.bind(this);
    this.handleCloseClick = this.handleCloseClick.bind(this);

    this.bindCoupons();
    this.bindCta();
    this.autoStart();
  }

  PopupInstance.prototype.bindCoupons = function () {
    var self = this;
    var buttons = this.root.querySelectorAll('[data-pcp-coupon-copy]');

    Array.prototype.forEach.call(buttons, function (button) {
      var labelEl = button.querySelector('[data-pcp-copy-label]');
      var copyText =
        (self.campaignsWrap && self.campaignsWrap.getAttribute('data-copy-button-text')) ||
        'Copy code';

      if (labelEl) labelEl.textContent = copyText;

      button.addEventListener('click', function () {
        self.handleCouponCopy(button);
      });
    });
  };

  PopupInstance.prototype.handleCouponCopy = function (button) {
    var self = this;
    var code = button.getAttribute('data-code') || '';
    var campaignEl = button.closest('[data-pcp-campaign]');
    var statusEl = campaignEl
      ? campaignEl.querySelector('[data-pcp-coupon-status]')
      : null;
    var labelEl = button.querySelector('[data-pcp-copy-label]');
    var copiedText =
      (this.campaignsWrap && this.campaignsWrap.getAttribute('data-copied-text')) ||
      'Copied!';
    var copyText =
      (this.campaignsWrap && this.campaignsWrap.getAttribute('data-copy-button-text')) ||
      'Copy code';

    function onCopied() {
      button.classList.add('is-copied');
      if (labelEl) labelEl.textContent = copiedText;
      if (statusEl) statusEl.textContent = copiedText;

      dispatch('pcp:coupon_copy', {
        sectionId: self.sectionId,
        campaignId: campaignEl ? campaignEl.id : null,
        code: code
      });

      window.setTimeout(function () {
        button.classList.remove('is-copied');
        if (labelEl) labelEl.textContent = copyText;
        if (statusEl) statusEl.textContent = '';
      }, COPY_RESET_MS);
    }

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(code).then(onCopied, function () {
        self.legacyCopy(code);
        onCopied();
      });
    } else {
      this.legacyCopy(code);
      onCopied();
    }
  };

  PopupInstance.prototype.legacyCopy = function (code) {
    var textarea = document.createElement('textarea');
    textarea.value = code;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'absolute';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    try {
      document.execCommand('copy');
    } catch (err) {
      /* Clipboard unavailable — coupon code remains visible for manual copy */
    }
    document.body.removeChild(textarea);
  };

  PopupInstance.prototype.bindCta = function () {
    var self = this;
    var ctas = this.root.querySelectorAll('[data-pcp-cta]');

    Array.prototype.forEach.call(ctas, function (cta) {
      cta.addEventListener('click', function () {
        var campaignEl = cta.closest('[data-pcp-campaign]');
        dispatch('pcp:cta_click', {
          sectionId: self.sectionId,
          campaignId: campaignEl ? campaignEl.id : null,
          url: cta.getAttribute('href')
        });
      });
    });
  };

  PopupInstance.prototype.autoStart = function () {
    var popupEnabled = this.root.getAttribute('data-popup-enabled') === 'true';
    var hasCampaigns = this.root.getAttribute('data-has-campaigns') === 'true';

    if (!popupEnabled || !hasCampaigns) return;

    if (!isDesignMode() && sessionGet(this.sessionKey)) return;

    var pageKey = PAGE_TYPE_MAP[this.root.getAttribute('data-page-type')] || null;
    var device = getDeviceType();
    var eligible = getEligibleCampaigns(this.root, pageKey, device);

    if (!eligible.length) return;

    this.selectCampaign(eligible[0]);

    var self = this;
    this.delayTimer = window.setTimeout(function () {
      self.open();
    }, this.delay * 1000);
  };

  PopupInstance.prototype.selectCampaign = function (campaignEl) {
    var campaigns = this.root.querySelectorAll('[data-pcp-campaign]');

    Array.prototype.forEach.call(campaigns, function (el) {
      el.hidden = true;
      el.setAttribute('aria-hidden', 'true');
    });

    campaignEl.hidden = false;
    campaignEl.setAttribute('aria-hidden', 'false');
    this.activeCampaign = campaignEl;

    dispatch('pcp:campaign_view', {
      sectionId: this.sectionId,
      campaignId: campaignEl.id
    });
  };

  PopupInstance.prototype.open = function () {
    if (this.isOpen) return;

    window.clearTimeout(this.closeTimer);

    this.lastFocusedElement = document.activeElement;
    this.root.hidden = false;
    this.root.setAttribute('aria-hidden', 'false');
    this.popup.setAttribute('aria-hidden', 'false');

    /* Force reflow so the browser registers the un-hidden state before
       the .is-open class triggers the CSS transition. */
    // eslint-disable-next-line no-unused-expressions
    this.root.offsetHeight;

    var self = this;
    window.requestAnimationFrame(function () {
      self.root.classList.add('is-open');
    });

    this.isOpen = true;

    document.addEventListener('keydown', this.handleKeydown, true);
    this.overlay.addEventListener('click', this.handleOverlayClick);
    this.closeBtn.addEventListener('click', this.handleCloseClick);

    this.focusFirstElement();

    dispatch('pcp:popup_open', {
      sectionId: this.sectionId,
      campaignId: this.activeCampaign ? this.activeCampaign.id : null
    });
  };

  PopupInstance.prototype.close = function () {
    if (!this.isOpen) return;

    window.clearTimeout(this.delayTimer);

    this.root.classList.remove('is-open');
    this.root.setAttribute('aria-hidden', 'true');
    this.popup.setAttribute('aria-hidden', 'true');

    document.removeEventListener('keydown', this.handleKeydown, true);
    this.overlay.removeEventListener('click', this.handleOverlayClick);
    this.closeBtn.removeEventListener('click', this.handleCloseClick);

    var self = this;
    var finalized = false;

    function finalize() {
      if (finalized) return;
      finalized = true;
      self.root.hidden = true;
      self.popup.removeEventListener('transitionend', self.handlePopupTransitionEnd);
      window.clearTimeout(self.closeTimer);

      if (self.lastFocusedElement && typeof self.lastFocusedElement.focus === 'function') {
        self.lastFocusedElement.focus();
      }
    }

    this.handlePopupTransitionEnd = finalize;
    this.popup.addEventListener('transitionend', this.handlePopupTransitionEnd);
    this.closeTimer = window.setTimeout(finalize, TRANSITION_FALLBACK_MS);

    this.isOpen = false;

    if (!isDesignMode() && !this.forcedPreview) {
      sessionSet(this.sessionKey, '1');
    }

    dispatch('pcp:popup_close', {
      sectionId: this.sectionId,
      campaignId: this.activeCampaign ? this.activeCampaign.id : null
    });
  };

  PopupInstance.prototype.handleCloseClick = function () {
    this.close();
  };

  PopupInstance.prototype.handleOverlayClick = function (event) {
    if (event.target === this.overlay) {
      this.close();
    }
  };

  PopupInstance.prototype.handleKeydown = function (event) {
    if (event.key === 'Escape' || event.key === 'Esc') {
      event.preventDefault();
      this.close();
      return;
    }

    if (event.key === 'Tab') {
      this.trapFocus(event);
    }
  };

  PopupInstance.prototype.getFocusableElements = function () {
    var scope = this.popup;
    var nodes = scope.querySelectorAll(FOCUSABLE_SELECTOR);
    return Array.prototype.filter.call(nodes, function (el) {
      return el.offsetParent !== null || el === document.activeElement;
    });
  };

  PopupInstance.prototype.focusFirstElement = function () {
    var focusable = this.getFocusableElements();
    if (focusable.length) {
      focusable[0].focus();
    } else {
      this.popup.setAttribute('tabindex', '-1');
      this.popup.focus();
    }
  };

  PopupInstance.prototype.trapFocus = function (event) {
    var focusable = this.getFocusableElements();
    if (!focusable.length) return;

    var first = focusable[0];
    var last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  /* ------------------------------------------------------------------------
     Theme Editor support
     ------------------------------------------------------------------------ */

  PopupInstance.prototype.previewOpen = function (campaignEl) {
    this.forcedPreview = true;
    window.clearTimeout(this.delayTimer);
    if (campaignEl) {
      this.selectCampaign(campaignEl);
    } else if (!this.activeCampaign) {
      var pageKey = PAGE_TYPE_MAP[this.root.getAttribute('data-page-type')] || null;
      var eligible = getEligibleCampaigns(this.root, pageKey, getDeviceType());
      var fallback = eligible[0] || this.root.querySelector('[data-pcp-campaign]');
      if (fallback) this.selectCampaign(fallback);
    }
    this.open();
  };

  PopupInstance.prototype.destroy = function () {
    window.clearTimeout(this.delayTimer);
    window.clearTimeout(this.closeTimer);
    document.removeEventListener('keydown', this.handleKeydown, true);
    if (this.overlay) this.overlay.removeEventListener('click', this.handleOverlayClick);
    if (this.closeBtn) this.closeBtn.removeEventListener('click', this.handleCloseClick);
  };

  /* ------------------------------------------------------------------------
     Registry + init
     ------------------------------------------------------------------------ */

  var registry = {};
  window.PremiumCampaignPopupPro = window.PremiumCampaignPopupPro || { instances: registry };

  function initRoot(root) {
    if (root.getAttribute('data-pcp-initialized') === 'true') return;
    root.setAttribute('data-pcp-initialized', 'true');

    var instance = new PopupInstance(root);
    registry[root.getAttribute('data-section-id')] = instance;
  }

  function initAll(context) {
    var scope = context || document;
    var roots = scope.querySelectorAll('[data-pcp-root]');
    Array.prototype.forEach.call(roots, initRoot);
  }

  function teardownRoot(sectionId) {
    var instance = registry[sectionId];
    if (instance) {
      instance.destroy();
      delete registry[sectionId];
    }
  }

  /* Theme Editor lifecycle events (no-ops on the live storefront) */
  document.addEventListener('shopify:section:load', function (event) {
    var sectionId = event.detail && event.detail.sectionId;
    var root = event.target.querySelector
      ? event.target.querySelector('[data-pcp-root]')
      : null;

    if (root) {
      teardownRoot(sectionId);
      root.removeAttribute('data-pcp-initialized');
      initRoot(root);
    }
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var sectionId = event.detail && event.detail.sectionId;
    if (sectionId) teardownRoot(sectionId);
  });

  document.addEventListener('shopify:section:select', function (event) {
    var sectionId = event.detail && event.detail.sectionId;
    var instance = registry[sectionId];
    if (instance) instance.previewOpen(null);
  });

  document.addEventListener('shopify:block:select', function (event) {
    var blockId = event.detail && event.detail.blockId;
    if (!blockId) return;

    var campaignEl = document.getElementById(blockId);
    if (!campaignEl) return;

    var root = campaignEl.closest('[data-pcp-root]');
    if (!root) return;

    var instance = registry[root.getAttribute('data-section-id')];
    if (instance) instance.previewOpen(campaignEl);
  });

  document.addEventListener('shopify:block:deselect', function (event) {
    var blockId = event.detail && event.detail.blockId;
    if (!blockId) return;

    var campaignEl = document.getElementById(blockId);
    if (!campaignEl) return;

    var root = campaignEl.closest('[data-pcp-root]');
    if (!root) return;

    var instance = registry[root.getAttribute('data-section-id')];
    if (instance) instance.close();
  });

  /* Boot */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }
})();